<template>
  <v-layout
    no-gutters
    class="spectralis-browser-body oct-browser-body escalon-absolute-cover"
    :style="ieStyle">
    <v-flex shrink class="sidebar-controls tall-content-wrapper">
      <oct-reference-image
        :style="referenceImageWrapperHeight"
        :isLoading="refImgLoading || octDataLoading"
        :refImg="refImg"
        :refFit="refFit"
        :refSize="refSize"
        :refImgNotFound="refImgNotFound"
        :refImgLocation="refImgLocation"
        :slices="slices"
        :sliceCurrent="sliceCurrent"
        :sliceBounds="sliceBounds"
        :sliceCurrentIndex="sliceCurrentIndex"
        :isPlaying="isPlaying"
        :playRate="playRate"
        :showExtension="true"
        :drawAllSlices="drawAllSlices"
        :isSpectralisCircle="isSpectralisCircle"
        :isSpectralisRadial="isSpectralisRadial"
        :isSpectralisRadialCircle="isSpectralisRadialCircle"
        :showOverlay="showThicknessMap"
        :overlayOpacity="thicknessMapOpacity"
        :overlayImg="thicknessMapImg"
        :overlayColorSettings="thicknessMapColorSettings"
        @previous="previousSlice"
        @next="nextSlice"
        @play="play"
        @pause="pause"
        @setPlayRate="setPlayRate"
        @selectSlice="selectSlice"/>
      <spectralis-color-buttons/>
      <image-zoom-control
        :isFlipped="false"
        :img="sliceImg"
        :imgIsLoading="isLoading"
        :imgNotFound="sliceImgNotFound"
        :transform="transform"
        :size="zoomControlSize"
        :fit="zoomControlFit"
        @updateZoom="updateZoom"
        @updateCenter="updateCenter"/>
      <circle-image-button
        size="xSmall"
        title="Reset to Default"
        @click="zoomToFit"
        btnClass="primary"
        :image="coloredAssetUrls.reset"/>
    </v-flex>
    <v-flex shrink class="main-view tall-content-wrapper" id="spectralis-main-view">
      <v-layout column>
        <v-flex shrink>
          <oct-toolbar
            :isSpectralis="true" />
        </v-flex>
        <v-flex grow spectralis-main-view-container>
          <spectralis-main-view
            :sliceSize="sliceSize"
            :sliceImgSize="sliceImgSize"
            :sliceFit="sliceFit"
            :transform="transform"
            :chartSize="chartSize"
            :realLocation="realLocation"
            :isInBounds="isInBounds"
            :innerChartSize="innerChartSize"
            @previous="previousSlice"
            @next="nextSlice"/>
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import octReferenceImage from "./OCTReferenceImage";
import octToolbar from "./OCTToolbar";
import spectralisColorButtons from "./SpectralisColorButtons";
import circleImageButton from "../../controls/CircleImageButton";
import spectralisMainView from "./SpectralisMainView";
import imageZoomControl from "../controls/ImageZoomControl";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";
import { mapWatchers } from "../../../helpers/utils";
import {
  defaultSize,
  defaultTransform,
  getViewSize,
  setSize,
  setFittedSize,
  setFitZoom,
  setOffsets,
  viewOffset,
  calculateRealLocation,
  withinViewBounds,
  setZoom,
  setCenter,
  setSpectralisReferenceFit,
  setTransformSizes,
  setZoomBounds
} from "../../../canvas/geometry";
import { mapGetters } from "vuex";
export default {
  name: "spectralisBrowserBody",
  components: {
    octReferenceImage,
    spectralisColorButtons,
    imageZoomControl,
    octToolbar,
    spectralisMainView,
    circleImageButton
  },
  mixins: [responsiveCanvases],
  data() {
    return {
      refSize: defaultSize(),
      refFit: defaultSize(),
      zoomControlSize: defaultSize(),
      zoomControlFit: defaultSize(),
      sliceSize: defaultSize(),
      sliceFit: defaultSize(),
      chartSize: defaultSize(),
      innerChartSize: defaultSize(),
      sliceImgSize: defaultSize(),
      transform: defaultTransform(),
      refImgTransform: defaultTransform(),
      useFitZoom: true,
      referenceWrapperId: "reference-image-wrapper",
      thicknessWrapperId: "reference-palette-wrapper",
      mainViewId: "spectralis-main-view",
      sliceWrapperId: "slice-image-wrapper",
      chartWrapperId: "spectralis-chart-wrapper",
      zoomWrapperId: "zoom-canvas-wrapper"
    };
  },
  computed: {
    ...mapGetters([
      "refImg",
      "refImgLoading",
      "refImgNotFound",

      "slices",
      "sliceCurrent",
      "sliceBounds",
      "sliceCurrentIndex",
      "sliceImg",
      "sliceImgNotFound",
      "sliceImgLoading",
      "sliceInfo",
      "isPlaying",
      "playRate",
      "octDataLoading",
      "heightLg",
      "heightMd",
      "isIE",
      "imageBrowserContext",
      "showThicknessMap",
      "drawAllSlices",
      "thicknessMapOpacity","thicknessMapImg","thicknessMapColorSettings"
    ]),
    isLoading() {
      return this.sliceImgLoading || this.octDataLoading;
    },
    imgOffset() {
      // calculate the position of the fitted main view relative to its container
      // for this browser, the main view is fitted in the bottom center,
      //  with an extra 70 px of padding on the left
      const x = Math.abs(this.sliceFit.w - this.sliceSize.w) / 2 + 70;
      const y = Math.abs(this.sliceFit.h - this.sliceSize.h);
      return { x, y };
    },
    ieStyle() {
      if (!this.isIE) return null;
      return "margin-top: 35px";
    },
    isSpectralisCircle(){
      return this.imageBrowserContext.groupName.toLowerCase().includes("circle") && !this.imageBrowserContext.groupName.toLowerCase().includes("radialcircles");
    },
    isSpectralisRadial(){
      return this.imageBrowserContext.groupName.toLowerCase().includes("star");
    },
    isSpectralisRadialCircle(){
      return this.imageBrowserContext.groupName.toLowerCase().includes("radialcircles");
    },
    referenceImageWrapperHeight(){
      if(this.slices && this.slices.length > 1){
        return "height: 376px;";
      }
      return "height: 300px;";
    }
  },
  methods: {
    keyboardEvent(e){
      if(!this.isSpectralisCircle && !this.isSpectralisRadial && !this.isSpectralisRadialCircle){
        if(e.keyCode == 37 || e.keyCode == 38) {
          this.nextSlice();
        }else if(e.keyCode == 39 || e.keyCode == 40){
          this.previousSlice();        
        }
      } 
    },
    updateCanvasSizes() {
      // reference control size
      let rvs = getViewSize(this.referenceWrapperId);
      setSize(this.refSize, rvs);
      setSpectralisReferenceFit(this.refSize, this.sliceBounds, this.refFit);

      let mvs = getViewSize(this.mainViewId);
      let infoSize = this.sliceInfo && this.sliceInfo.dimensions;
      if (infoSize) {
        setSize(this.sliceImgSize, infoSize);
        // left padding provides enough room for labels
        let leftPadding = 100;
        let heightRespectingAspectRatio =
          infoSize.pixelHeight * ((mvs.w - leftPadding) / infoSize.pixelWidth);
        let sliceHeight;
        if (this.heightLg)
          sliceHeight = Math.min(575, heightRespectingAspectRatio);
        else if(this.heightMd)
          sliceHeight = Math.min(575, heightRespectingAspectRatio);
        else
          sliceHeight = Math.min(335, heightRespectingAspectRatio);
        let svs = {
          w: mvs.w,
          h: sliceHeight
        };
        setSize(this.sliceSize, svs);
        // respect the dimensions of the main slice image within the main view
        let sFit = setFittedSize(
          this.sliceSize,
          this.sliceImgSize,
          this.sliceFit,
          { left: leftPadding }
        );

        // chart view size
        let remainingHeight = mvs.h - svs.h;
        let chartHeight;
        if (this.heightLg)
          chartHeight = Math.min(275, remainingHeight);
        else if(this.heightMd)
          chartHeight = Math.min(275, remainingHeight);
        else
          chartHeight = Math.min(210, remainingHeight);
        // the chart must be the size of the main view + the left side labels
        setSize(this.chartSize, {
          w: this.sliceFit.w + leftPadding,
          h: chartHeight - 15
        });
        // the inner chart must be the same size as the main view
        setSize(this.innerChartSize, {
          w: this.sliceFit.w,
          h: this.chartSize.h - 70
        });

        setTransformSizes(this.refImgTransform, this.refFit, this.refFit);
        setTransformSizes(this.transform, this.sliceFit, this.sliceImgSize);
        // set minimum zoom to fit the image into the canvas
        setZoomBounds(this.transform, sFit.zoom, 5);
        setFitZoom(this.transform, sFit.zoom, this.useFitZoom);
        // correct the zoom if it is smaller than the minimum zoom
        setZoom(this.transform, this.transform.fitZoom);
        setOffsets(this.transform);
      }

      // zoom control size
      let zcs = getViewSize(this.zoomWrapperId);
      setSize(this.zoomControlSize, zcs);
      setFittedSize(
        this.zoomControlSize,
        this.sliceImgSize,
        this.zoomControlFit
      );
    },
    refImgLocation(x, y) {
      const offset = viewOffset(this.refSize, this.refFit);
      if (!withinViewBounds(x, y, this.refSize, offset))
        return { x: -1, y: -1 };
      let loc = calculateRealLocation(x, y, offset, this.refImgTransform);
      return { x: loc.x / this.refFit.w, y: loc.y / this.refFit.h };
    },
    realLocation(x, y) {
      if (!this.withinViewBounds(x, y, this.sliceSize, this.imgOffset))
        return { x: -1, y: -1 };
      return calculateRealLocation(x, y, this.imgOffset, this.transform);
    },
    withinViewBounds(x, y, viewSize = { w: 0, h: 0 }, offset = { x: 0, y: 0 }) {
      // returns true if the position is within a box offset from the center of a view
      // assumes that the fit view is in the bottom right corner
      return (
        x >= offset.x &&
        y >= offset.y &&
        x <= viewSize.w - 30 &&
        y <= viewSize.h
      );
    },
    isInBounds(x, y) {
      return (
        x >= 0 && y >= 0 && x <= this.sliceImgSize.w && y <= this.sliceImgSize.h
      );
    },
    resetTransform() {
      this.transform = defaultTransform();
      setZoomBounds(this.transform, 1, 5);
      this.updateZoom(1);
    },
    updateZoom(zoom) {
      this.useFitZoom = false;
      setZoom(this.transform, zoom);
      setOffsets(this.transform);
    },
    updateCenter(center) {
      this.useFitZoom = false;
      setCenter(this.transform, center.x, center.y);
      setOffsets(this.transform);
    },
    zoomToFit() {
      this.useFitZoom = true;
      setZoom(this.transform, this.transform.fitZoom);
      setOffsets(this.transform);
    },
    previousSlice() {
      this.$store.dispatch("selectPreviousSlice");
    },
    nextSlice() {
      this.$store.dispatch("selectNextSlice");
    },
    play() {
      this.$store.dispatch("playSlices");
    },
    pause() {
      this.$store.commit("pause");
    },
    setPlayRate(rate) {
      this.$store.commit("setPlayRate", rate);
    },
    selectSlice(index) {
      this.$store.dispatch("selectOCTImageByIndex", {
        keyword: "slice",
        index
      });
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo"], function() {
      this.updateCanvasSizes();
    })
  },
  mounted() {
    this.resetTransform();
    this.updateCanvasSizes();
    document.addEventListener("keydown", this.keyboardEvent);
  },
  destroyed () {
    document.removeEventListener("keydown", this.keyboardEvent);
  }
};
</script>